import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { CreateReportPayload, CreateReportResponse, Institution, Report, ReportListResponse, ReportSearchCriteria } from "./report.model";
import { environment } from "../../../environments/environment";

const sortByName = <T extends { name?: string | null }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, {
      sensitivity: 'base',
    }),
  );

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private readonly runtimeApiUrl = (window as { __APP_CONFIG__?: { apiUrl?: string } }).__APP_CONFIG__?.apiUrl;
  private readonly baseApiUrl = this.runtimeApiUrl || environment.apiUrl;
  private readonly apiUrl = `${this.baseApiUrl}/reports`;
  private readonly institutionApiUrl = `${this.baseApiUrl}/institutions`;

  constructor(private http: HttpClient) { }

  getInstitutions(): Observable<Institution[]> {
    return this.http.get<any>(this.institutionApiUrl).pipe(
      map(response => {
        let instList: any[] = [];
        if (Array.isArray(response)) {
          instList = response;
        } else if (response && Array.isArray(response.institutions)) {
          instList = response.institutions;
        } else if (response && Array.isArray(response.return_body)) {
          instList = response.return_body;
        }

        return sortByName(instList).map((inst, index) => ({
          id: String(inst.pk || inst.id || index),
          name: inst.name || inst.institutionName || inst.displayName || 'Unknown',
        }));
      })
    );
  }

  getReports(criteria?: ReportSearchCriteria): Observable<Report[]> {
    let params = new HttpParams();

    if (criteria) {
      if (criteria.reportId) params = params.set('reportId', criteria.reportId);
      if (criteria.reportName) params = params.set('reportName', criteria.reportName);
      if (criteria.institution) params = params.set('institution', criteria.institution);
      if (criteria.active) params = params.set('status', criteria.active === 'Yes' ? 'true' : 'false');
      if (criteria.reportType) params = params.set('reportType', criteria.reportType);
      if (criteria.internalReport) params = params.set('internalReport', criteria.internalReport === 'Yes' ? 'true' : 'false');
    }

    return this.http.get<ReportListResponse>(this.apiUrl, { params }).pipe(
      map(response => response.reports.map(r => this.mapToReport(r)))
    );
  }

  createReport(report: Report): Observable<CreateReportResponse> {
    const payload = this.mapToCreatePayload(report);
    return this.http.post<CreateReportResponse>(this.apiUrl, payload);
  }

  getReportById(id: string): Observable<Report> {
    return this.http.get<any>(`${this.apiUrl}/${id}`).pipe(
      map(r => this.mapToReport(r.report || r))
    );
  }

  updateReport(report: Report): Observable<CreateReportResponse> {
    const payload = this.mapToCreatePayload(report);
    console.log('Updating report with payload:', payload);
    return this.http.put<CreateReportResponse>(`${this.apiUrl}/${report.id}`, payload);
  }

  deleteReport(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  private mapToCreatePayload(report: Report): CreateReportPayload {
    const pageName = String(report.pageName ?? '').trim();

    return {
      reportName: report.reportName,
      role: report.role,
      pageName: pageName,
      pbiUnitId: report.pbiUnitId,
      pbiWorkspace: report.pbiWorkspace,
      pbiDataset: report.pbiDataset,
      active: report.active === 'Yes',
      internalReport: report.internalReport === 'Yes',
      reportType: report.reportType,
      pageView: report.pageView,
      filterPaneEnabled: report.filterPaneEnabled,
      navContentPaneEnabled: report.navContentPaneEnabled,
      selectedInstitutions: report.assignedInstitutions.map(i => String(i.id)),
    };
  }

  private mapToReport(apiReport: any): Report {
    return {
      id: apiReport.pk || apiReport.reportId || apiReport.id,
      reportId: apiReport.reportId || '',
      reportName: String(apiReport?.reportName ?? ''),
      role: String(apiReport?.role ?? ''),
      pageName: String(apiReport?.pageName ?? ''),
      pbiUnitId: apiReport.pbiUnitId || '',
      pbiWorkspace: apiReport.pbiWorkspace || '',
      pbiDataset: apiReport.pbiDataset || '',
      active: apiReport.active === true || apiReport.active === 'Yes' ? 'Yes' : 'No',
      internalReport: apiReport.internalReport === true || apiReport.internalReport === 'Yes' ? 'Yes' : 'No',
      reportType: apiReport.reportType === 'RLS' ? 'RLS' : 'Non-RLS',
      pageView: (() => { const pv = apiReport.pageView ?? apiReport.page_view; return pv === 'fitToPage' ? 'fitToPage' : pv === 'fitToWidth' ? 'fitToWidth' : 'actualSize'; })(),
      filterPaneEnabled: !!apiReport.filterPaneEnabled,
      navContentPaneEnabled: !!apiReport.navContentPaneEnabled,
      assignedInstitutions: (Array.isArray(apiReport.institutions) ? apiReport.institutions
        : Array.isArray(apiReport.selectedInstitutions) ? apiReport.selectedInstitutions
        : []).map((i: any) => ({
        id: String(typeof i === 'string' ? i : i.id || i.pk),
        name: typeof i === 'string' ? '' : i.name || '',
      })),
    };
  }
}